import styled, { createGlobalStyle, css } from 'styled-components';
import { theme } from '../theme';

export const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; }
  html, body, #root {
    height: 100%;
    margin: 0;
    overflow: hidden;
  }
  body {
    font-family: ${theme.fonts.body};
    background: ${theme.colors.bgPrimary};
    color: ${theme.colors.text};
  }
  body::before {
    content: '';
    position: fixed;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(ellipse 80% 50% at 10% 0%, rgba(157, 78, 221, 0.22), transparent 55%),
      radial-gradient(ellipse 60% 40% at 95% 10%, rgba(76, 201, 240, 0.14), transparent 50%),
      radial-gradient(ellipse 50% 30% at 50% 100%, rgba(255, 20, 147, 0.08), transparent 45%);
    z-index: 0;
  }
  a { color: ${theme.colors.pink}; text-decoration: none; }
  input, select, textarea, button { font-family: inherit; }
  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-thumb {
    background: rgba(255, 20, 147, 0.35);
    border-radius: 99px;
  }
`;

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  width: 100%;
  min-height: 100%;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const PageHeaderMain = styled.div`
  min-width: 0;
`;

export const Title = styled.h1`
  font-family: ${theme.fonts.heading};
  font-size: clamp(1.35rem, 2vw, 1.65rem);
  color: ${theme.colors.pink};
  text-shadow: ${theme.colors.glow};
  margin: 0;
  letter-spacing: 0.5px;
`;

export const Subtitle = styled.p`
  margin: 0.35rem 0 0;
  color: ${theme.colors.textMuted};
  font-size: 0.88rem;
  line-height: 1.45;
  max-width: 720px;
`;

export const PageActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

export const Surface = styled.section`
  position: relative;
  background: linear-gradient(145deg, rgba(28, 20, 52, 0.92), rgba(16, 11, 32, 0.88));
  border: 1px solid rgba(255, 20, 147, 0.2);
  border-radius: 14px;
  padding: 0;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, ${theme.colors.pink}, ${theme.colors.purple});
    opacity: 0.85;
  }
`;

export const SurfaceHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem 0.75rem 1.15rem;
  border-bottom: 1px solid rgba(255, 20, 147, 0.12);
  flex-wrap: wrap;
`;

export const SurfaceTitle = styled.h3`
  margin: 0;
  font-size: 0.92rem;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.3px;
`;

export const SurfaceBody = styled.div<{ $flush?: boolean }>`
  padding: ${({ $flush }) => ($flush ? '0' : '0.85rem 1rem 0.85rem 1.15rem')};
`;

export const Card = Surface;
export const PanelGrid = styled.div<{ $cols?: string }>`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || '1fr'};
  gap: 0.85rem;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const buttonSizes = {
  sm: css`padding: 0.38rem 0.65rem; font-size: 0.75rem;`,
  md: css`padding: 0.55rem 0.95rem; font-size: 0.85rem;`,
};

export const Button = styled.button<{ $variant?: 'primary' | 'danger' | 'ghost'; $size?: 'sm' | 'md' }>`
  border: none;
  border-radius: 9px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.18s ease;
  white-space: nowrap;
  ${({ $size }) => buttonSizes[$size || 'md']}
  background: ${({ $variant }) =>
    $variant === 'danger' ? theme.colors.danger :
    $variant === 'ghost' ? 'rgba(255,255,255,0.03)' :
    `linear-gradient(135deg, ${theme.colors.pink}, ${theme.colors.purpleDeep})`};
  color: ${({ $variant }) => ($variant === 'ghost' ? theme.colors.textMuted : '#fff')};
  border: ${({ $variant }) => ($variant === 'ghost' ? `1px solid rgba(255, 20, 147, 0.22)` : 'none')};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${theme.colors.glow};
    color: ${({ $variant }) => ($variant === 'ghost' ? '#fff' : '#fff')};
  }
  &:disabled { opacity: 0.45; cursor: not-allowed; transform: none; box-shadow: none; }
`;

export const ActionBar = styled.div`
  display: inline-flex;
  gap: 0.35rem;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
`;

export const Toolbar = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  flex-wrap: wrap;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.55rem 0.75rem;
  border-radius: 9px;
  border: 1px solid rgba(255, 20, 147, 0.22);
  background: rgba(10, 7, 22, 0.65);
  color: ${theme.colors.text};
  font-size: 0.88rem;
  &:focus {
    outline: none;
    border-color: ${theme.colors.pink};
    box-shadow: 0 0 0 2px rgba(255, 20, 147, 0.15);
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 0.55rem 0.75rem;
  border-radius: 9px;
  border: 1px solid rgba(255, 20, 147, 0.22);
  background: rgba(10, 7, 22, 0.65);
  color: ${theme.colors.text};
  font-size: 0.88rem;
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  padding: 0.55rem 0.75rem;
  border-radius: 9px;
  border: 1px solid rgba(255, 20, 147, 0.22);
  background: rgba(10, 7, 22, 0.65);
  color: ${theme.colors.text};
  resize: vertical;
  font-size: 0.88rem;
`;

export const TableWrap = styled.div`
  width: 100%;
  overflow: auto;
  max-height: calc(100vh - 220px);
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;

  th, td {
    padding: 0.55rem 0.65rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 20, 147, 0.1);
    vertical-align: middle;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: rgba(22, 14, 42, 0.98);
    color: ${theme.colors.pinkSoft};
    font-weight: 600;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  tbody tr:hover td {
    background: rgba(255, 20, 147, 0.05);
  }

  td { color: ${theme.colors.textMuted}; }
`;

export const Badge = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ $active }) => ($active ? 'rgba(125, 207, 76, 0.18)' : 'rgba(255, 77, 109, 0.18)')};
  color: ${({ $active }) => ($active ? theme.colors.success : theme.colors.danger)};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(125, 207, 76, 0.35)' : 'rgba(255, 77, 109, 0.35)')};
`;

export const Grid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols || 1}, minmax(0, 1fr));
  gap: 0.85rem;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

export const Label = styled.label`
  display: block;
  font-size: 0.78rem;
  color: ${theme.colors.textMuted};
  margin-bottom: 0.3rem;
  letter-spacing: 0.2px;
`;

export const FormRow = styled.div`
  margin-bottom: 0.75rem;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

export const Muted = styled.p`
  color: ${theme.colors.textMuted};
  font-size: 0.82rem;
  margin: 0.25rem 0 0;
  line-height: 1.45;
`;

export const ErrorText = styled.p`
  color: ${theme.colors.danger};
  font-size: 0.82rem;
  margin: 0.35rem 0 0;
`;

export const TabBar = styled.div`
  display: inline-flex;
  gap: 0.35rem;
  padding: 0.25rem;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 11px;
  border: 1px solid rgba(255, 20, 147, 0.15);
`;

export const TabBtn = styled.button<{ $active?: boolean }>`
  border: none;
  border-radius: 8px;
  padding: 0.45rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.15s ease;
  background: ${({ $active }) => ($active ? 'linear-gradient(135deg, rgba(255,20,147,0.35), rgba(123,44,191,0.35))' : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : theme.colors.textMuted)};
  box-shadow: ${({ $active }) => ($active ? theme.colors.glow : 'none')};
`;

export const Toast = styled.div`
  padding: 0.55rem 0.85rem;
  border-radius: 9px;
  background: rgba(76, 201, 240, 0.12);
  border: 1px solid rgba(76, 201, 240, 0.35);
  color: ${theme.colors.blue};
  font-size: 0.84rem;
`;

export const StatCard = styled.div`
  padding: 1rem 1.1rem;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 20, 147, 0.08), rgba(123, 44, 191, 0.06));
  border: 1px solid rgba(255, 20, 147, 0.18);
`;

export const StatValue = styled.div`
  font-family: ${theme.fonts.heading};
  font-size: 1.75rem;
  color: #fff;
  margin-top: 0.35rem;
`;
